import { useCallback, useEffect, useState } from "react";
import {
  buildInternalLaunchProofView,
  type InternalLaunchProofViewV1,
} from "@enrein/shared-contracts";
import type { StudioApiClient } from "./api.js";
import {
  buildLaunchProofInputFromStudioApis,
  loadStudioLaunchProofApiSnapshot,
} from "./build-launch-proof-input-from-studio-apis.js";
import badgeUrl from "@enrein/design-system/assets/enrein-badge-launch-proof.svg";

function phase1StatusLabel(status: InternalLaunchProofViewV1["phase1Sections"][number]["status"]): string {
  switch (status) {
    case "pass":
      return "Passed";
    case "warn":
      return "Warnings";
    case "fail":
      return "Failed";
    case "not_evaluated":
      return "Not evaluated";
    default:
      return "Not evaluated";
  }
}

export function LaunchProofPanel({
  api,
  projectId,
}: {
  readonly api: StudioApiClient;
  readonly projectId: string | null;
}) {
  const [view, setView] = useState<InternalLaunchProofViewV1 | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setBusy(true);
    setError(null);
    try {
      try {
        const persisted = await api.getCurrentInternalLaunchProof(projectId);
        setView(persisted);
        return;
      } catch (persistErr) {
        const message = persistErr instanceof Error ? persistErr.message : String(persistErr);
        if (
          !message.includes("launch_proof_internal_not_ready") &&
          !message.includes("studio_api_error_404")
        ) {
          throw persistErr;
        }
      }

      const snapshot = await loadStudioLaunchProofApiSnapshot(api, projectId);
      const input = buildLaunchProofInputFromStudioApis(projectId, snapshot);
      setView(
        buildInternalLaunchProofView({
          ...input,
          proofId: `lp_studio_${projectId}`,
        }),
      );
    } catch (err) {
      setView(null);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [api, projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!projectId) {
    return <p className="empty-state">Select a project to view Launch Proof.</p>;
  }

  return (
    <div>
      <p>
        Internal Launch Proof summary - display only. When a persisted Launch Proof exists from export,
        it is shown here; otherwise sections show not evaluated until export completes.
      </p>
      <div className="button-row">
        <button type="button" onClick={() => void load()} disabled={busy}>
          Refresh Launch Proof
        </button>
      </div>
      {error ? <p className="error">{error}</p> : null}
      {view ? (
        <article className="report-card proof-card">
          <div className="proof-card-header">
            <img src={badgeUrl} alt="" className="proof-badge" width={48} height={48} aria-hidden="true" />
            <div>
              <h3>{view.headline}</h3>
              <p>
                Reference: {view.proofReferenceLabel} - Aggregate: {view.aggregateStatus}
              </p>
            </div>
          </div>
          {view.searchReadinessExcerpt ? (
            <>
              <h4>Search readiness excerpt</h4>
              <p>{view.searchReadinessExcerpt.headline}</p>
              {view.searchReadinessExcerpt.staleBanner ? (
                <p className="warning">{view.searchReadinessExcerpt.staleBanner}</p>
              ) : null}
            </>
          ) : (
            <p>No search readiness report loaded for this project.</p>
          )}
          {view.departureKitSummary ? <p>{view.departureKitSummary}</p> : null}
          <h4>Phase 1 surface quality (display only)</h4>
          <ul>
            {view.phase1Sections.map((section) => (
              <li key={section.checkId}>
                {section.areaLabel}: {phase1StatusLabel(section.status)} - {section.clientSafeSummary}
                {section.internalSummary ? ` (${section.internalSummary})` : null}
              </li>
            ))}
          </ul>
          <h4>Disclaimers</h4>
          <ul>
            {view.launchProofDisclaimers.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </article>
      ) : busy ? (
        <div className="loading-panel">
          <p>Loading Launch Proof...</p>
        </div>
      ) : null}
    </div>
  );
}
